// ============= Imports =============
import { useState, useCallback, useRef, useMemo } from 'react';
import { fetchClients } from '../apis/aderant/get/clients';
import { fetchMatters } from '../apis/aderant/get/matters';
import { UseAuth } from '../contexts/AuthContext';
import { createDebounce } from '../utils/debounce';

// ============= Types =============
export interface Client {
  clientName: string;
  clientCode: string;
  clientUno: string;
}

export interface Matter {
  clientUno: string;
  clntMattCode: string;
  matterCode: string;
  matterName: string;
  matterUno: string;
}

export interface FormattedClientData {
  label: string;
  value: string;
  clientName: string;
  clientCode: string;
}

export interface FormattedMatterData {
  label: string;
  value: string;
  matterName: string;
  matterCode: string;
  clientUno: string;
}

// ============= Constants =============
export const NO_CLIENTS_FOUND = 'No Clients Found';
export const NO_MATTERS_FOUND = 'No Matters Found';

const getResponseItems = (response: {
  success: boolean;
  data?: { items?: unknown[] };
}): unknown[] => {
  if (!response.success) return [];
  return response.data?.items ?? [];
};

// ============= Hook =============
export const useClientMatterSearch = (office?: string) => {
  // Debug logging to see if the hook is being recreated
  console.log('🔍 useClientMatterSearch hook called with office:', office);

  // Memoize the office to prevent unnecessary re-renders
  const memoizedOffice = useMemo(() => office, [office]);

  // State management
  const { getAccessToken } = UseAuth();
  const [clientData, setClientData] = useState<string[]>([]);
  const [formattedClientData, setFormattedClientData] = useState<
    FormattedClientData[]
  >([]);
  const [formattedMatterData, setFormattedMatterData] = useState<
    FormattedMatterData[]
  >([]);
  const [matterData, setMatterData] = useState<string[]>([]);
  const [selectedClientUno, setSelectedClientUno] = useState<string>('');
  const [selectedMatterUno, setSelectedMatterUno] = useState<string>('');
  const [selectedClientLabel, setSelectedClientLabel] = useState<string>('');

  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [isLoadingMatters, setIsLoadingMatters] = useState(false);

  // Use ref to track the latest search value
  const lastSearchRefClient = useRef<string>('');
  const lastSearchRefMatter = useRef<string>('');

  // ============= Formatters =============

  const formatClientData = useCallback(
    (client: Client): FormattedClientData => ({
      label: `${client.clientName} - (${client.clientCode})`,
      value: client.clientUno,
      clientName: client.clientName,
      clientCode: client.clientCode,
    }),
    [],
  );

  const formatMatterData = useCallback(
    (matter: Matter): FormattedMatterData => ({
      label: `${matter.matterName} - (${matter.clntMattCode})`,
      value: matter.matterUno,
      matterName: matter.matterName,
      matterCode: matter.matterCode,
      clientUno: matter.clientUno,
    }),
    [],
  );

  const applyClientSearchResults = useCallback(
    (items: Client[]) => {
      if (items.length === 0) {
        setFormattedClientData([]);
        setClientData([NO_CLIENTS_FOUND]);
        return;
      }

      const formattedData = items.map(formatClientData);
      setFormattedClientData(formattedData);
      setClientData(formattedData.map((client) => client.label));
    },
    [formatClientData],
  );

  const applyMatterSearchResults = useCallback(
    (items: Matter[]) => {
      if (items.length === 0) {
        setFormattedMatterData([]);
        setMatterData([NO_MATTERS_FOUND]);
        return;
      }

      const formattedData = items.map(formatMatterData);
      setFormattedMatterData(formattedData);
      setMatterData(formattedData.map((matter) => matter.label));
    },
    [formatMatterData],
  );

  // ============= Debounced Functions =============

  const debouncedClientSearch = createDebounce(async (searchValue: string) => {
    console.log('🔍 useClientMatterSearch.debouncedClientSearch called:', {
      searchValue,
    });
    console.log('🔍 About to check if search value changed');
    // Skip if the search value hasn't changed
    if (lastSearchRefClient.current === searchValue) {
      console.log('🔍 Search value unchanged, skipping');
      return;
    }
    console.log('🔍 Search value changed, proceeding');
    lastSearchRefClient.current = searchValue;

    if (!searchValue.trim()) {
      setFormattedClientData([]);
      setClientData([]);
      setIsLoadingClients(false);
      return;
    }

    try {
      setIsLoadingClients(true);
      const response = await fetchClients({
        getAccessToken,
        searchValue: searchValue,
        office: memoizedOffice,
      });

      if (lastSearchRefClient.current !== searchValue) return;

      applyClientSearchResults(getResponseItems(response) as Client[]);
    } catch (error) {
      console.error('Error fetching clients:', error);
      setFormattedClientData([]);
      setClientData([]);
    } finally {
      if (lastSearchRefClient.current === searchValue) {
        setIsLoadingClients(false);
      }
    }
  }, 300); // 300ms delay

  const debouncedMatterSearch = createDebounce(async (searchValue: string) => {
    if (lastSearchRefMatter.current === searchValue) return;
    lastSearchRefMatter.current = searchValue;

    if (!searchValue.trim()) {
      setFormattedMatterData([]);
      setMatterData([]);
      setIsLoadingMatters(false);
      return;
    }

    try {
      setIsLoadingMatters(true);
      const response = await fetchMatters({
        getAccessToken,
        searchValue: searchValue,
        office: memoizedOffice,
      });

      if (lastSearchRefMatter.current !== searchValue) return;

      applyMatterSearchResults(getResponseItems(response) as Matter[]);
    } catch (error) {
      console.error('Error fetching matters:', error);
      setFormattedMatterData([]);
      setMatterData([]);
    } finally {
      if (lastSearchRefMatter.current === searchValue) {
        setIsLoadingMatters(false);
      }
    }
  }, 300);

  // ============= Handlers Search =============

  const handleMatterSearch = useCallback(
    (searchValue: string) => {
      // If we have data and the search is empty, don't trigger loading
      if (!searchValue.trim() && formattedMatterData.length > 0) {
        setFormattedMatterData([]);
        setMatterData([]);
        setIsLoadingMatters(false);
        return;
      }

      debouncedMatterSearch(searchValue);
    },
    [debouncedMatterSearch, formattedMatterData.length],
  );

  const handleClientSearch = useCallback(
    (searchValue: string) => {
      console.log('🔍 useClientMatterSearch.handleClientSearch called:', {
        searchValue,
      });
      console.log('🔍 About to call debouncedClientSearch');

      // If we have data and the search is empty, don't trigger loading
      if (!searchValue.trim() && formattedClientData.length > 0) {
        setFormattedClientData([]);
        setClientData([]);
        setIsLoadingClients(false);
        return;
      }

      debouncedClientSearch(searchValue);
      console.log('🔍 debouncedClientSearch called');
    },
    [debouncedClientSearch, formattedClientData.length],
  );

  // ============= Handlers Select =============

  const handleClientSelect = useCallback(
    async (selectedOption: string) => {
      if (selectedOption === NO_CLIENTS_FOUND) return;

      const selectedClient = formattedClientData.find(
        (client) => client.label === selectedOption,
      );

      if (!selectedClient) return;

      try {
        setIsLoadingMatters(true);
        setSelectedClientUno(selectedClient.value);
        setSelectedClientLabel(selectedClient.label);
        const response = await fetchMatters({
          getAccessToken,
          searchValue: selectedClient.value,
          office: memoizedOffice,
        });

        applyMatterSearchResults(getResponseItems(response) as Matter[]);
      } catch (error) {
        console.error('Error fetching matters:', error);
        setFormattedMatterData([]);
        setMatterData([]);
      } finally {
        setIsLoadingMatters(false);
      }
    },
    [
      formattedClientData,
      applyMatterSearchResults,
      memoizedOffice,
      getAccessToken,
    ],
  );

  const handleMatterSelect = useCallback(
    async (selectedOption: string) => {
      if (selectedOption === NO_MATTERS_FOUND) return;

      const selectedMatter = formattedMatterData.find(
        (matter) => matter.label === selectedOption,
      );

      if (!selectedMatter) return;

      try {
        setIsLoadingClients(true);
        setSelectedMatterUno(selectedMatter.value);
        const response = await fetchClients({
          getAccessToken,
          searchValue: selectedMatter.clientUno,
          office: memoizedOffice,
        });

        const items = getResponseItems(response) as Client[];
        if (items.length === 0) {
          applyClientSearchResults([]);
        } else {
          const formattedData = items.map(formatClientData);
          setFormattedClientData(formattedData);
          setClientData(formattedData.map((client) => client.clientName));
          setSelectedClientUno(formattedData[0]?.value);
          setSelectedClientLabel(formattedData[0]?.label || '');
        }
      } catch (error) {
        console.error('Error fetching clients:', error);
        setFormattedClientData([]);
        setClientData([]);
      } finally {
        setIsLoadingClients(false);
      }
    },
    [
      formattedMatterData,
      memoizedOffice,
      formatClientData,
      applyClientSearchResults,
      getAccessToken,
    ],
  );

  return {
    clientData,
    matterData,
    formattedClientData,
    formattedMatterData,
    selectedClientUno,
    selectedMatterUno,
    selectedClientLabel,
    handleClientSearch,
    handleClientSelect,
    handleMatterSearch,
    handleMatterSelect,
    isLoadingClients,
    setIsLoadingClients,
    isLoadingMatters,
    setIsLoadingMatters,
  };
};
